import Code from '@/components/dashboard/Code';
import SectionActions from '@/docs/SectionActions';
import SEOMetadata from '@/components/SEOMetadata';
import DocHeading from '@/docs/DocHeading';
import { useRef } from 'react'

export default function VisualizationDoc() {
  const sectionRef = useRef(null);
  return (
    <div className="api-documentation space-y-8 lg:max-w-[calc(100vw-340px)] md:max-w-[calc(100vw-80px)] max-w-[calc(100vw-30px)] overflow-hidden md:overflow-visible pb-50">
      <SEOMetadata title="Visualization Methods" description="A collection of visualization functions for financial and data analysis using Plotly.js.
          These methods provide interactive charts and graphs for data exploration and presentation."  canonicalUrl="https://axionquant.com/docs/client-libraries/python-SDK/visualizations" />
      <div className="mb-10">
        <DocHeading className="text-3xl font-bold text-gray-900 mb-4">Visualization Methods</DocHeading>
        <p className="text-lg text-gray-700 max-w-[900px]">
          A collection of visualization functions for financial and data analysis using Plotly.js.
          These methods provide interactive charts and graphs for data exploration and presentation.
        </p>
      </div>

      <div className="space-y-12" ref={sectionRef}>
        {/* visualize - Core Helper */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">visualize (Core Helper)</h2>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                  HELPER
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  visualize.visualize(fig)
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Displays a Plotly figure object. This is the core visualization function used internally by all other methods.
                In Jupyter notebooks, this will render the chart inline. In scripts, it will open the chart in a browser.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Required</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">fig</td>
                      <td className="px-4 py-3 text-sm text-gray-700">plotly.graph_objects.Figure</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                          Required
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        Plotly figure object to display
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="figure" contentRef={sectionRef} />
              <h3 className="text-sm font-semibold mb-3">Example</h3>
              <Code>{`# Create a simple figure
import plotly.graph_objects as go
fig = go.Figure(data=go.Scatter(x=[1,2,3], y=[4,5,6]))

# Display it
visualize.visualize(fig)`}</Code>
            </div>
          </div>
        </div>

        {/* cov */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">visualize.cov()</h2>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  FUNCTION
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  visualize.cov(df)
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Creates a heatmap visualization of the correlation matrix for numeric columns in a DataFrame.
                Uses the Viridis color scale and automatically handles the correlation calculation.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Required</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">df</td>
                      <td className="px-4 py-3 text-sm text-gray-700">pandas.DataFrame</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                          Required
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        DataFrame containing numeric columns for correlation analysis. Non-numeric columns are automatically ignored.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="line" contentRef={sectionRef} />
              <h3 className="text-sm font-semibold mb-3">Example</h3>
              <Code>{`import pandas as pd
from axion import visualize

# Create sample data
df = pd.DataFrame({
    'A': [1, 2, 3, 4, 5],
    'B': [2, 4, 6, 8, 10],
    'C': [5, 4, 3, 2, 1],
    'D': [1, 3, 5, 7, 9]
})

# Display correlation heatmap
visualize.cov(df)`}</Code>
            </div>
          </div>
        </div>

        {/* candles */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">visualize.candles()</h2>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                  FUNCTION
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  visualize.candles(df)
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Creates an interactive candlestick chart for financial price data with OHLC (Open, High, Low, Close) values.
                Perfect for stock prices, cryptocurrency data, or any time-series financial data.
              </p>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Required DataFrame Columns</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <ul className="text-sm text-gray-700 space-y-2">
                  <li><code className="text-gray-800 bg-gray-100 px-2 py-1 rounded">time</code> - Time series data (dates/timestamps)</li>
                  <li><code className="text-gray-800 bg-gray-100 px-2 py-1 rounded">open</code> - Opening price for each period</li>
                  <li><code className="text-gray-800 bg-gray-100 px-2 py-1 rounded">high</code> - Highest price during each period</li>
                  <li><code className="text-gray-800 bg-gray-100 px-2 py-1 rounded">low</code> - Lowest price during each period</li>
                  <li><code className="text-gray-800 bg-gray-100 px-2 py-1 rounded">close</code> - Closing price for each period</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="candlestick" contentRef={sectionRef} />
              <h3 className="text-sm font-semibold mb-3">Example</h3>
              <Code>{`import pandas as pd
from axion import visualize

# Sample stock data
df = pd.DataFrame({
    'time': ['2024-01-01', '2024-01-02', '2024-01-03'],
    'open': [100, 102, 101],
    'high': [105, 103, 104],
    'low': [99, 101, 100],
    'close': [102, 101, 103]
})

# Display candlestick chart
visualize.candles(df)`}</Code>
            </div>
          </div>
        </div>

        {/* line */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">visualize.line()</h2>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                  FUNCTION
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  visualize.line(df, x, y, log=False)
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Creates a line chart for time series or sequential data visualization. Supports logarithmic scaling for the x-axis.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Default</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">df</td>
                      <td className="px-4 py-3 text-sm text-gray-700">pandas.DataFrame</td>
                      <td className="px-4 py-3 text-sm text-gray-700">-</td>
                      <td className="px-4 py-3 text-sm text-gray-700">DataFrame containing the data to plot</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">x</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm text-gray-700">-</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Column name for x-axis values (typically time/date)</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">y</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm text-gray-700">-</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Column name for y-axis values</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">log</td>
                      <td className="px-4 py-3 text-sm text-gray-700">boolean</td>
                      <td className="px-4 py-3 text-sm text-gray-700">False</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Use logarithmic scale for x-axis</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="line-charts" contentRef={sectionRef} />
              <h3 className="text-sm font-semibold mb-3">Examples</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Basic Line Chart</p>
                <Code>{`# Basic line chart
df = pd.DataFrame({
    'date': ['2024-01-01', '2024-01-02', '2024-01-03'],
    'value': [10, 15, 13]
})
visualize.line(df, x='date', y='value')`}</Code>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Logarithmic X-Axis</p>
                <Code>{`# Line chart with logarithmic x-axis
df = pd.DataFrame({
    'x': [1, 10, 100, 1000],
    'y': [1, 2, 3, 4]
})
visualize.line(df, x='x', y='y', log=True)`}</Code>
              </div>
            </div>
          </div>
        </div>

        {/* pie */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">visualize.pie()</h2>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  FUNCTION
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  visualize.pie(df, values, labels)
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Creates a pie chart showing proportional distribution of categorical data. Each slice represents a category,
                and the size corresponds to the numeric values.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Required</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">df</td>
                      <td className="px-4 py-3 text-sm text-gray-700">pandas.DataFrame</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                          Required
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">DataFrame containing the data</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">values</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                          Required
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">Column name for pie slice sizes (numeric)</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">labels</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                          Required
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">Column name for pie slice labels (categorical)</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="pie-chart" contentRef={sectionRef} />
              <h3 className="text-sm font-semibold mb-3">Example</h3>
              <Code>{`# Market share data
df = pd.DataFrame({
    'company': ['Apple', 'Samsung', 'Google', 'Others'],
    'market_share': [45, 30, 15, 10]
})

visualize.pie(df, values='market_share', labels='company')`}</Code>
            </div>
          </div>
        </div>

        {/* fit */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">visualize.fit()</h2>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                  FUNCTION
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  visualize.fit(df, x, y, log=False, hover=[], group=None)
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Creates a scatter plot with an Ordinary Least Squares (OLS) trendline for regression analysis.
                The trendline shows the linear relationship between variables with confidence bands.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Default</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">df</td>
                      <td className="px-4 py-3 text-sm text-gray-700">pandas.DataFrame</td>
                      <td className="px-4 py-3 text-sm text-gray-700">-</td>
                      <td className="px-4 py-3 text-sm text-gray-700">DataFrame containing the data</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">x</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm text-gray-700">-</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Column name for x-axis values (independent variable)</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">y</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm text-gray-700">-</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Column name for y-axis values (dependent variable)</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">log</td>
                      <td className="px-4 py-3 text-sm text-gray-700">boolean</td>
                      <td className="px-4 py-3 text-sm text-gray-700">False</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Use logarithmic scale for x-axis</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">hover</td>
                      <td className="px-4 py-3 text-sm text-gray-700">list</td>
                      <td className="px-4 py-3 text-sm text-gray-700">[]</td>
                      <td className="px-4 py-3 text-sm text-gray-700">List of column names to show in hover tooltips</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">group</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm text-gray-700">None</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Column name for color grouping (creates separate trendlines per group)</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="scatter" contentRef={sectionRef} />
              <h3 className="text-sm font-semibold mb-3">Examples</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">With Grouping</p>
                <Code>{`# Simple linear regression
df = pd.DataFrame({
    'x': [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    'y': [2, 4, 5, 4, 5, 7, 8, 9, 10, 12],
    'category': ['A', 'A', 'A', 'A', 'B', 'B', 'B', 'B', 'B', 'B']
})

# With grouping
visualize.fit(df, x='x', y='y', group='category', hover=['category'])

# Without grouping
visualize.fit(df, x='x', y='y')`}</Code>
              </div>
            </div>
          </div>
        </div>

        {/* scatter */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">visualize.scatter()</h2>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                  FUNCTION
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  visualize.scatter(df, x, y, log=False, hover=[], group=None)
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Creates a standard scatter plot for visualizing relationships between two variables.
                Supports grouping and hover data for enhanced data exploration.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Default</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">df</td>
                      <td className="px-4 py-3 text-sm text-gray-700">pandas.DataFrame</td>
                      <td className="px-4 py-3 text-sm text-gray-700">-</td>
                      <td className="px-4 py-3 text-sm text-gray-700">DataFrame containing the data</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">x</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm text-gray-700">-</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Column name for x-axis values</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">y</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm text-gray-700">-</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Column name for y-axis values</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">log</td>
                      <td className="px-4 py-3 text-sm text-gray-700">boolean</td>
                      <td className="px-4 py-3 text-sm text-gray-700">False</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Use logarithmic scale for x-axis</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">hover</td>
                      <td className="px-4 py-3 text-sm text-gray-700">list</td>
                      <td className="px-4 py-3 text-sm text-gray-700">[]</td>
                      <td className="px-4 py-3 text-sm text-gray-700">List of column names to show in hover tooltips</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">group</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm text-gray-700">None</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Column name for color grouping</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="scatter-grouped" contentRef={sectionRef} />
              <h3 className="text-sm font-semibold mb-3">Examples</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">With Grouping</p>
                <Code>{`# Scatter plot with grouping and hover data
df = pd.DataFrame({
    'height': [150, 160, 170, 180, 155, 165, 175, 185],
    'weight': [50, 60, 70, 80, 55, 65, 75, 85],
    'gender': ['F', 'F', 'M', 'M', 'F', 'F', 'M', 'M'],
    'age': [25, 30, 35, 40, 28, 33, 38, 43]
})

visualize.scatter(df, x='height', y='weight', 
                  group='gender', hover=['age'])`}</Code>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Simple Scatter</p>
                <Code>{`# Simple scatter
visualize.scatter(df, x='height', y='weight')`}</Code>
              </div>
            </div>
          </div>
        </div>

        {/* bar */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">visualize.bar()</h2>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  FUNCTION
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  visualize.bar(df, x, y)
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Creates a vertical bar chart for categorical data comparison. Each bar represents a category,
                and the height represents the corresponding value.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Required</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">df</td>
                      <td className="px-4 py-3 text-sm text-gray-700">pandas.DataFrame</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                          Required
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">DataFrame containing the data</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">x</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                          Required
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">Column name for categories (x-axis)</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">y</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                          Required
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">Column name for values (bar heights)</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="bar-chart" contentRef={sectionRef} />
              <h3 className="text-sm font-semibold mb-3">Example</h3>
              <Code>{`# Sales by product
df = pd.DataFrame({
    'product': ['A', 'B', 'C', 'D'],
    'sales': [1500, 2300, 1200, 3100]
})

visualize.bar(df, x='product', y='sales')`}</Code>
            </div>
          </div>
        </div>

        {/* area */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">visualize.area()</h2>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                  FUNCTION
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  visualize.area(df, x, y, group, sub="")
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Creates a stacked area chart showing cumulative values over time by category.
                Great for visualizing composition changes over time.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Default</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">df</td>
                      <td className="px-4 py-3 text-sm text-gray-700">pandas.DataFrame</td>
                      <td className="px-4 py-3 text-sm text-gray-700">-</td>
                      <td className="px-4 py-3 text-sm text-gray-700">DataFrame containing the data</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">x</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm text-gray-700">-</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Column name for x-axis (typically time/date)</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">y</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm text-gray-700">-</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Column name for y-axis values</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">group</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm text-gray-700">-</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Column name for grouping/categories</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">sub</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm text-gray-700">""</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Subgroup identifier (currently unused)</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="stacked-bar" contentRef={sectionRef} />
              <h3 className="text-sm font-semibold mb-3">Example</h3>
              <Code>{`# Monthly sales by region
df = pd.DataFrame({
    'month': ['Jan', 'Jan', 'Feb', 'Feb', 'Mar', 'Mar'],
    'sales': [100, 150, 120, 180, 140, 200],
    'region': ['North', 'South', 'North', 'South', 'North', 'South']
})

visualize.area(df, x='month', y='sales', group='region')`}</Code>
            </div>
          </div>
        </div>

        {/* heatmap */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">visualize.heatmap()</h2>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                  FUNCTION
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  visualize.heatmap(df, x, y, hover=[])
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Creates a density heatmap showing the distribution of data points across two dimensions.
                The color intensity represents the density of points in each region.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Default</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">df</td>
                      <td className="px-4 py-3 text-sm text-gray-700">pandas.DataFrame</td>
                      <td className="px-4 py-3 text-sm text-gray-700">-</td>
                      <td className="px-4 py-3 text-sm text-gray-700">DataFrame containing the data</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">x</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm text-gray-700">-</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Column name for x-axis values</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">y</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm text-gray-700">-</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Column name for y-axis values</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">hover</td>
                      <td className="px-4 py-3 text-sm text-gray-700">list</td>
                      <td className="px-4 py-3 text-sm text-gray-700">[]</td>
                      <td className="px-4 py-3 text-sm text-gray-700">List of column names to show in hover tooltips</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="clustering" contentRef={sectionRef} />
              <h3 className="text-sm font-semibold mb-3">Example</h3>
              <Code>{`# Create sample data with clusters
import numpy as np
import SEOMetadata from '@/components/SEOMetadata';
import DocHeading from '@/docs/DocHeading';
np.random.seed(42)
df = pd.DataFrame({
    'x': np.random.normal(0, 1, 1000),
    'y': np.random.normal(0, 1, 1000),
    'value': np.random.randn(1000)
})

visualize.heatmap(df, x='x', y='y', hover=['value'])`}</Code>
            </div>
          </div>
        </div>

        {/* radar */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">visualize.radar()</h2>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  FUNCTION
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  visualize.radar(df, values, labels)
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Creates a radar chart for multivariate data visualization across multiple axes.
                Each axis represents a different variable, and the area shows the profile.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Required</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">df</td>
                      <td className="px-4 py-3 text-sm text-gray-700">pandas.DataFrame</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                          Required
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">DataFrame containing a single row of data</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">values</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                          Required
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">Column name containing the numeric values</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">labels</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                          Required
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">Column name containing the axis labels</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="radar" contentRef={sectionRef} />
              <h3 className="text-sm font-semibold mb-3">Example</h3>
              <Code>{`# Product performance metrics
df = pd.DataFrame({
    'metric': ['Speed', 'Reliability', 'Cost', 'Quality', 'Support'],
    'score': [85, 92, 78, 95, 88]
})

visualize.radar(df, values='score', labels='metric')`}</Code>
            </div>
          </div>
        </div>

        {/* barh */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">visualize.barh()</h2>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                  FUNCTION
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  visualize.barh(df, x, y)
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Creates a horizontal bar chart, useful for comparing categories with long names
                or when you have many categories. Bars extend horizontally from the y-axis.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Required</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">df</td>
                      <td className="px-4 py-3 text-sm text-gray-700">pandas.DataFrame</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                          Required
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">DataFrame containing the data</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">x</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                          Required
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">Column name for values (bar lengths)</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">y</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                          Required
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">Column name for categories (y-axis labels)</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="horizontal-bar" contentRef={sectionRef} />
              <h3 className="text-sm font-semibold mb-3">Example</h3>
              <Code>{`# Long category names
df = pd.DataFrame({
    'product': ['Enterprise License', 'Professional Edition', 
                'Standard Package', 'Basic Subscription'],
    'revenue': [150000, 85000, 45000, 20000]
})

visualize.barh(df, x='revenue', y='product')`}</Code>
            </div>
          </div>
        </div>

        {/* spread */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">visualize.spread()</h2>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                  FUNCTION
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  visualize.spread(dfs, x, y)
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Creates a composite chart showing two series and their difference (spread) as a bar chart.
                Perfect for pairs trading analysis or comparing two related time series.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Required</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">dfs</td>
                      <td className="px-4 py-3 text-sm text-gray-700">list of DataFrames</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                          Required
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">List containing two DataFrames to compare</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">x</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                          Required
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">Common column name for merging and x-axis (usually time/date)</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">y</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                          Required
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">Column name for values in both DataFrames</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="comparison" contentRef={sectionRef} />
              <h3 className="text-sm font-semibold mb-3">Example</h3>
              <Code>{`# Compare two correlated stocks
df1 = pd.DataFrame({
    'date': ['2024-01-01', '2024-01-02', '2024-01-03'],
    'price': [100, 102, 101]
})

df2 = pd.DataFrame({
    'date': ['2024-01-01', '2024-01-02', '2024-01-03'],
    'price': [98, 101, 103]
})

visualize.spread([df1, df2], x='date', y='price')`}</Code>
            </div>
          </div>
        </div>

        {/* polls */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">visualize.polls()</h2>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  FUNCTION
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  visualize.polls(df)
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Creates a multi-line chart where each column in the DataFrame becomes a separate line series.
                The x-axis uses the DataFrame index, making it ideal for time series with multiple variables.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Required</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">df</td>
                      <td className="px-4 py-3 text-sm text-gray-700">pandas.DataFrame</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                          Required
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        DataFrame where each column becomes a line series. Index is used for x-axis.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="area-chart" contentRef={sectionRef} />
              <h3 className="text-sm font-semibold mb-3">Example</h3>
              <Code>{`# Opinion polling over time
df = pd.DataFrame({
    'Candidate A': [45, 47, 46, 48, 50, 49],
    'Candidate B': [42, 41, 43, 42, 41, 43],
    'Undecided': [13, 12, 11, 10, 9, 8]
}, index=['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'])

visualize.polls(df)`}</Code>
            </div>
          </div>
        </div>

        {/* tree */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">visualize.tree()</h2>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                  FUNCTION
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  visualize.tree(df)
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Creates a hierarchical treemap visualization for nested categorical data.
                Rectangles represent hierarchy levels, with size proportional to marketCap and color indicating pctchange.
              </p>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Expected DataFrame Columns</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <ul className="text-sm text-gray-700 space-y-2">
                  <li><code className="text-gray-800 bg-gray-100 px-2 py-1 rounded">sector</code> - Top-level category (e.g., Technology, Healthcare)</li>
                  <li><code className="text-gray-800 bg-gray-100 px-2 py-1 rounded">industry</code> - Mid-level category (e.g., Software, Biotech)</li>
                  <li><code className="text-gray-800 bg-gray-100 px-2 py-1 rounded">symbol</code> - Leaf-level identifier (e.g., AAPL, MSFT)</li>
                  <li><code className="text-gray-800 bg-gray-100 px-2 py-1 rounded">marketCap</code> - Size value (determines rectangle area)</li>
                  <li><code className="text-gray-800 bg-gray-100 px-2 py-1 rounded">pctchange</code> - Color/hover data (red-green color scale)</li>
                  <li><code className="text-gray-800 bg-gray-100 px-2 py-1 rounded">lastsale</code> - Additional hover data (last sale price)</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="treemap" contentRef={sectionRef} />
              <h3 className="text-sm font-semibold mb-3">Example</h3>
              <Code>{`# Stock market sector visualization
df = pd.DataFrame({
    'sector': ['Tech', 'Tech', 'Tech', 'Health', 'Health'],
    'industry': ['Software', 'Software', 'Hardware', 'Biotech', 'Pharma'],
    'symbol': ['AAPL', 'MSFT', 'NVDA', 'GILD', 'PFE'],
    'marketCap': [2500000, 2100000, 800000, 700000, 180000],
    'pctchange': [2.5, 1.8, -0.5, 3.2, -1.1],
    'lastsale': [175.50, 380.25, 450.75, 85.30, 42.60]
})

visualize.tree(df)`}</Code>
            </div>
          </div>
        </div>

        {/* graph */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">visualize.graph()</h2>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                  FUNCTION
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  visualize.graph(df, x, bars=[], lines=[], areas=[], title='', color=None)
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Creates a comprehensive composite chart combining bars, lines, and areas on the same plot.
                Offers maximum flexibility for complex visualizations with multiple series types.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Default</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">df</td>
                      <td className="px-4 py-3 text-sm text-gray-700">pandas.DataFrame</td>
                      <td className="px-4 py-3 text-sm text-gray-700">-</td>
                      <td className="px-4 py-3 text-sm text-gray-700">DataFrame containing all data</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">x</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm text-gray-700">-</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Column name for x-axis values</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">bars</td>
                      <td className="px-4 py-3 text-sm text-gray-700">list</td>
                      <td className="px-4 py-3 text-sm text-gray-700">[]</td>
                      <td className="px-4 py-3 text-sm text-gray-700">List of column names for bar series</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">lines</td>
                      <td className="px-4 py-3 text-sm text-gray-700">list</td>
                      <td className="px-4 py-3 text-sm text-gray-700">[]</td>
                      <td className="px-4 py-3 text-sm text-gray-700">List of column names for line series</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">areas</td>
                      <td className="px-4 py-3 text-sm text-gray-700">list</td>
                      <td className="px-4 py-3 text-sm text-gray-700">[]</td>
                      <td className="px-4 py-3 text-sm text-gray-700">List of column names for area series</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">title</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm text-gray-700">''</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Chart title</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">color</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm text-gray-700">None</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Column name for color grouping bars</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Color Palette</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <pre className="text-sm text-gray-700">
                  <code>{`shades_of_white = [
    'rgb(31, 119, 180)',  // blue
    'rgb(255, 127, 14)',  // orange
    'rgb(44, 160, 44)',   // green
    'rgb(214, 39, 40)',   // red
    'rgb(148, 103, 189)', // purple
    'rgb(140, 86, 75)',   // brown
    'rgb(255, 255, 255)',
    'rgb(245, 245, 245)',
    'rgb(235, 235, 235)',
    'rgb(235, 225, 225)',
    'rgb(235, 215, 215)',
    'rgb(235, 205, 205)',
]`}</code>
                </pre>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="composite" contentRef={sectionRef} />
              <h3 className="text-sm font-semibold mb-3">Examples</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Composite Chart</p>
                <Code>{`# Composite chart with bars and lines
df = pd.DataFrame({
    'date': ['2024-01-01', '2024-01-02', '2024-01-03'],
    'revenue': [1000, 1200, 1100],
    'profit': [200, 250, 230],
    'users': [500, 600, 650],
    'region': ['North', 'South', 'North']
})

visualize.graph(df, x='date', 
                bars=['revenue'], 
                lines=['profit', 'users'],
                title='Company Performance')`}</Code>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">With Color Grouping</p>
                <Code>{`# With color grouping for bars
visualize.graph(df, x='date', 
                bars=['revenue'], 
                lines=['profit'],
                color='region',
                title='Performance by Region')`}</Code>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
